import React from 'react';
import { VoiceLiveConnectionState } from '../types/voiceLive.types';

interface ConnectionStatusIndicatorProps {
  connectionState: VoiceLiveConnectionState;
  className?: string;
  onReconnect?: () => void;
}

export const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  connectionState,
  className = '',
  onReconnect
}) => {
  if (connectionState === 'connected') {
    return null; // Don't show indicator when connected
  }

  const getStatusInfo = () => {
    switch (connectionState) {
      case 'connecting':
        return {
          text: 'Connecting...',
          bgColor: 'bg-yellow-500',
          icon: '⏳',
          showReconnectButton: false
        };
      case 'reconnecting':
        return {
          text: 'Reconnecting...',
          bgColor: 'bg-orange-500',
          icon: '🔄',
          showReconnectButton: false
        };
      case 'disconnected':
        return {
          text: 'Connection lost',
          bgColor: 'bg-red-500',
          icon: '⚠️',
          showReconnectButton: true
        };
      case 'error':
        return {
          text: 'Connection error',
          bgColor: 'bg-red-600',
          icon: '❌',
          showReconnectButton: true
        };
      default:
        return {
          text: 'Unknown status',
          bgColor: 'bg-gray-500',
          icon: '❓',
          showReconnectButton: false
        };
    }
  };

  const { text, bgColor, icon, showReconnectButton } = getStatusInfo();

  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 ${className}`}>
      <div className={`${bgColor} text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 ${(connectionState === 'connecting' || connectionState === 'reconnecting') ? 'animate-pulse' : ''}`}>
        <span className="text-sm">{icon}</span>
        <span className="text-sm font-medium">{text}</span>
        {(connectionState === 'connecting' || connectionState === 'reconnecting') && (
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        )}
        {showReconnectButton && onReconnect && (
          <button
            onClick={onReconnect}
            className="ml-2 bg-white text-gray-800 text-xs px-2 py-1 rounded hover:bg-gray-100 transition-colors"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};